import React, { useEffect, useRef } from 'react';
import { X } from 'lucide-react';

interface VideoModalProps {
    isOpen: boolean;
    onClose: () => void;
    videoSrc: string;
}

const VideoModal: React.FC<VideoModalProps> = ({ isOpen, onClose, videoSrc }) => {
    const videoRef = useRef<HTMLVideoElement>(null);

    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };

        if (isOpen) {
            document.addEventListener('keydown', handleEscape);
            document.body.style.overflow = 'hidden';
        } else {
            // Pause video when modal closes
            videoRef.current?.pause();
        }

        return () => {
            document.removeEventListener('keydown', handleEscape);
            document.body.style.overflow = 'unset';
        };
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    const isYouTube = !videoSrc.startsWith('/') && !videoSrc.startsWith('http') === false
        ? false
        : !videoSrc.startsWith('/');

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6">
            <div
                className="absolute inset-0 bg-background-dark/90 backdrop-blur-sm transition-opacity"
                onClick={onClose}
            ></div>

            <div className="relative w-full max-w-5xl bg-black rounded-2xl shadow-2xl shadow-primary/20 overflow-hidden ring-1 ring-white/10 animate-in fade-in zoom-in duration-200">
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 z-10 p-2 bg-black/50 hover:bg-white/10 rounded-full text-white/70 hover:text-white transition-all"
                >
                    <X className="w-6 h-6" />
                </button>

                <div className="relative aspect-video bg-black">
                    {isYouTube ? (
                        <iframe
                            className="w-full h-full"
                            src={`https://www.youtube.com/embed/${videoSrc}?autoplay=1&rel=0`}
                            title="FlowSpec QA Demo"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                            allowFullScreen
                        />
                    ) : (
                        <video
                            ref={videoRef}
                            className="w-full h-full"
                            src={videoSrc}
                            controls
                            autoPlay
                            playsInline
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default VideoModal;
